import dayjs from 'dayjs';
import toPairs from 'lodash/toPairs';

import { stripOperatorPrefix } from '@/app/community/composables/utils/filter-operator';
import {
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_ROUTE_NAMES,
  COMMUNITY_ROUTE_PRESET_FILTERS,
  DEFAULT_COMMUNITY_INTERACTION_SORT,
  INBOX_SORT_BY,
  UNREAD_QUERY_FILTER_KEYS,
} from '@/app/community/constants';
import { FILTER_OPERATOR } from '@/app/community/constants/communityFilters';
import { useIdentityStore } from '@/stores/identity';
import { useTrackingStore } from '@/stores/tracking';
import { applyReversibleUpdate } from '@/utils/optimism';

const COUNTS_LIST_FIELDS = new Set([
  'brandIds',
  'tagIds',
  'mediaIds',
  'types',
  'assigneeIds',
  'interactionRuleIds',
  'messageContent',
  'subtype',
]);

const COUNTS_BOOLEAN_OPERATOR_FIELDS = new Set(['isSpam', 'isQuestion']);

const OPERATOR_PREFIX_PATTERN = /^(OR_NOT|AND_NOT|OR|AND):/;

function parseOperatorAndValues(value) {
  if (Array.isArray(value)) return { operator: null, values: value };
  if (typeof value !== 'string' || value === '') return { operator: null, values: [] };
  const match = value.match(OPERATOR_PREFIX_PATTERN);
  const operator = match ? match[1] : null;
  const raw = operator ? value.slice(match[0].length) : value;
  return { operator, values: raw.split(',').filter((s) => s !== '') };
}

export const parseBoolean = (s) => {
  if (typeof s === 'boolean') {
    return s;
  }
  return ['true', 'false'].includes(s) ? s === 'true' : undefined;
};

export const getCommunityInteractionFilters = (query, ignoreAssigneeId = true) => {
  const identityStore = useIdentityStore();
  const { isArchived, isFollowUp, responseRecommended, isReplied, isDeleted } = query;
  const assignment = ignoreAssigneeId ? {} : { assigneeId: identityStore?.identity?.id };
  return {
    isArchived: parseBoolean(isArchived),
    ...(responseRecommended !== true ? {} : { responseRecommended }),
    ...(isFollowUp === undefined ? {} : { isFollowUp: parseBoolean(isFollowUp) }),
    ...(isReplied !== true ? {} : { isReplied }),
    ...(isDeleted !== true ? {} : { isDeleted }),
    ...assignment,
  };
};

export const getCommunityInteractionSort = (query) =>
  query.sort || DEFAULT_COMMUNITY_INTERACTION_SORT;

export const getNumericIdsFromQuery = (query, key) => {
  const idQuery = query?.[key] ?? [];
  const ids = Array.isArray(idQuery) ? idQuery : idQuery.split(',');
  return ids.map((id) => Number(id));
};

export const sortCommunityInteractions = (communityInteractions, query) => {
  const sort = getCommunityInteractionSort(query);

  if (sort === INBOX_SORT_BY.RELEVANCE) {
    return communityInteractions;
  }

  let direction;

  if (sort === INBOX_SORT_BY.SRC_UPDATED_AT_DESC) {
    direction = -1;
  }

  if (sort === INBOX_SORT_BY.SRC_UPDATED_AT_ASC) {
    direction = 1;
  }

  return communityInteractions.sort((prev, next) => {
    const p1 = prev.sourceUpdatedAt ?? prev.createdAt;
    const p2 = next.sourceUpdatedAt ?? next.createdAt;
    if (direction === 1) {
      return p1 < p2 ? -1 : 1;
    }
    return p1 < p2 ? 1 : -1;
  });
};

// Apply the Message Type filter (types + subtype) with OR / OR_NOT semantics.
export const filterMessageTypeMatch = (interactions, query) => {
  const { operator: typesOp, value: typesValue } = stripOperatorPrefix(query.types);
  const { operator: subtypeOp, value: subtypeValue } = stripOperatorPrefix(query.subtype);
  const isExclude = (typesOp ?? subtypeOp) === FILTER_OPERATOR.OR_NOT;

  // Ad comments share BE `type` with regular comments (BE stores both as `*_COMMENT`
  // and uses `is_ad_comment` to differentiate). For OR we keep the long-standing permissive
  // matching (strip `_ADS` so `INSTAGRAM_COMMENT` filter keeps ads too.
  // For OR_NOT we MUST honor `is_ad_comment` or we silently drop ad comments
  // when the user only meant to exclude regular ones (and vice versa).
  //
  // Split into an array so `.includes` is an exact-match check — string substring matching
  // produces false positives (e.g. `'TIKTOK_COMMENT_MENTION'.includes('TIKTOK_COMMENT')`).
  const defaultTypes = subtypeValue ? [] : Object.values(COMMUNITY_INTERACTION_TYPES);
  const interactionTypes = typesValue ? typesValue.replace(/_ADS/g, '').split(',') : defaultTypes;
  const rawTypes = typesValue ? typesValue.split(',') : null;
  const subtypes = subtypeValue ? subtypeValue.split(',') : null;

  return interactions.filter((interaction) => {
    const subtypeHit = !!subtypes?.includes(interaction?.subtype);

    if (isExclude && rawTypes) {
      const isAd = interaction.isAdComment === true;
      const typeHit = rawTypes.some((filterType) => {
        const filterTargetsAds = filterType.includes('_ADS');
        return interaction.type === filterType.replace('_ADS', '') && filterTargetsAds === isAd;
      });
      return !(typeHit || subtypeHit);
    }

    const typeMatch = interactionTypes.includes(interaction.type) || subtypeHit;
    return isExclude ? !typeMatch : typeMatch;
  });
};

// Apply the Message Content filter (messageContent + isSpam + isQuestion) with OR / OR_NOT semantics.
export const filterMessageContentMatch = (interactions, query) => {
  const { operator: contentOp, value: contentValue } = stripOperatorPrefix(query.messageContent);
  const { operator: isSpamOp, value: isSpamValue } = stripOperatorPrefix(query.isSpam);
  const { operator: isQuestionOp, value: isQuestionValue } = stripOperatorPrefix(query.isQuestion);
  const isExclude = (contentOp ?? isSpamOp ?? isQuestionOp) === FILTER_OPERATOR.OR_NOT;

  return interactions.filter((interaction) => {
    const conditions = [];
    if (parseBoolean(isSpamValue)) conditions.push(interaction?.isSpam === true);
    if (parseBoolean(isQuestionValue)) conditions.push(interaction?.sentiment?.isQuestion === true);
    if (contentValue) {
      conditions.push(contentValue.split(',').includes(interaction?.messageContent));
    }
    if (conditions.length === 0) return true;
    const anyMatch = conditions.some(Boolean);
    return isExclude ? !anyMatch : anyMatch;
  });
};

export const filterCommunityInteractions = (
  communityInteractions,
  currentQuery,
  ignoreIsArchived = false,
  idFilters = [],
) => {
  const trackingStore = useTrackingStore();
  const routePresets = COMMUNITY_ROUTE_PRESET_FILTERS[trackingStore.currentRoute?.name] || {};
  const query = {};

  // Apply route presets first
  Object.keys(routePresets).forEach((key) => {
    query[key] = routePresets[key];
  });

  // Apply currentQuery, overriding presets with explicit values
  Object.keys(currentQuery).forEach((key) => {
    if (currentQuery[key] !== undefined) {
      query[key] = currentQuery[key];
    }
  });

  let tagOperator;
  let tagIds;
  if (query.tagIds) {
    [tagOperator, tagIds] = query.tagIds.split(':');
    tagIds = tagIds?.split(',').map((id) => Number(id)) || [];
  }

  const brandIds = getNumericIdsFromQuery(query, 'brandIds');
  const filters = getCommunityInteractionFilters(
    query,
    trackingStore.currentRoute?.name !== COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU,
  );
  // Apply filters
  // didn't figure out the cause but communityInteractions can have {0:undefined} that causes issue
  // later on, so added an undefined filter guard in the end.
  let filtered = communityInteractions
    .filter((interaction) =>
      toPairs(filters).every(([key, value]) => {
        // temp work around for isArchived out of sync in DB and ES. Expose issue to UI to
        // let users have a chance to fix them instead of infinite loading
        if ((ignoreIsArchived || value === undefined) && key === 'isArchived') return true;
        // raw interactions carry `deletedAt` (not `isDeleted`); derive on the fly.
        if (key === 'isDeleted') return !!interaction.deletedAt === value;
        return interaction[key] === value;
      }),
    )
    .filter((interaction) => interaction !== undefined);

  // Filter by tags
  if (tagIds?.length > 0) {
    const method = tagOperator === 'OR' ? 'some' : 'every';
    filtered = filtered.filter((interaction) =>
      tagIds[method]((tagId) => interaction.tagIds.includes(tagId)),
    );
  }

  // Filter by Message Type (types + subtype, OR / OR_NOT)
  filtered = filterMessageTypeMatch(filtered, query);

  // Filter by Message Content (messageContent + isSpam + isQuestion, OR / OR_NOT)
  filtered = filterMessageContentMatch(filtered, query);

  if (idFilters?.length > 0) {
    filtered = filtered.filter((interaction) => !idFilters.includes(interaction.id));
  }

  // Filter by date filters
  const { sourceUpdatedAfter: startDate, sourceUpdatedBefore: endDate } = query;
  if (startDate && endDate) {
    filtered = filtered.filter((interaction) => {
      let dateTime = interaction.sourceUpdatedAt ?? interaction.createdAt;
      // Below if is mainly a check for avoiding doing a large amount of test migrations
      if (!dayjs.isDayjs(dateTime)) {
        dateTime = dayjs(dateTime);
      }
      return dateTime.isBetween(
        dayjs(startDate).startOf('day'),
        dayjs(endDate).endOf('day'),
        'day',
        '[]',
      );
    });
  }
  // Filter by brands
  if (brandIds.length > 0) {
    filtered = filtered.filter((interaction) => brandIds.includes(interaction.brandId));
  }

  return filtered;
};

export const applyReversibleCommunityInteractionUpdate = (
  communityInteractions,
  id,
  { tagIds, ...flagUpdates },
) => {
  const communityInteraction = communityInteractions.find((ci) => ci.id === id);
  if (communityInteraction) {
    const addTagIds = tagIds?.add ?? [];
    const removeTagIds = tagIds?.remove ?? [];
    applyReversibleUpdate(communityInteractions, id, {
      ...flagUpdates,
      tagIds: communityInteraction.tagIds
        .filter((tid) => !removeTagIds.includes(tid) && !addTagIds.includes(tid))
        .concat(addTagIds),
    });
  }
};

export function getUnreadCountQueryParams(currentQuery) {
  return UNREAD_QUERY_FILTER_KEYS.reduce((queryParams, filterKey) => {
    // TODO: Remove brandId(s) checks once the multi brand inbox is fully rolled out
    if (filterKey === 'brandIds' && currentQuery.brandIds) {
      queryParams.brandIds = currentQuery.brandIds.split(',');
    } else {
      queryParams[filterKey] = currentQuery[filterKey];
    }
    return queryParams;
  }, {});
}

export function getCountsQueryParams(currentQuery) {
  const operators = {};
  const params = UNREAD_QUERY_FILTER_KEYS.reduce((queryParams, filterKey) => {
    const value = currentQuery[filterKey];
    if (value == null || value === '') return queryParams;
    if (COUNTS_LIST_FIELDS.has(filterKey)) {
      const { operator, values } = parseOperatorAndValues(value);
      if (!values.length) return queryParams;
      queryParams[filterKey] =
        filterKey === 'tagIds' ? values.map(Number).filter(Number.isFinite) : values;
      if (operator) operators[filterKey] = operator;
    } else if (COUNTS_BOOLEAN_OPERATOR_FIELDS.has(filterKey)) {
      if (typeof value === 'boolean') {
        queryParams[filterKey] = value;
      } else {
        const { operator, values } = parseOperatorAndValues(value);
        const bool = parseBoolean(values[0]);
        if (bool === undefined) return queryParams;
        queryParams[filterKey] = bool;
        if (operator) operators[filterKey] = operator;
      }
    } else {
      queryParams[filterKey] = value;
    }
    return queryParams;
  }, {});
  if (Object.keys(operators).length) params.operators = operators;
  return params;
}
